import { z } from 'zod';

export const journeyStateSchema = z.enum([
    'HIDDEN',
    'LOCKED',
    'NOT_STARTED',
    'ACTION',
    'PROCESSING',
    'WAITING',
    'FAILED',
    'DONE',
]);

export type JourneyState = z.infer<typeof journeyStateSchema>;

export const journeyAreaCodeSchema = z.enum(['KNOWLEDGE_BASE', 'WORK_ARCHITECTURE', 'AI_TRANSFORMATION']);

export type JourneyAreaCode = z.infer<typeof journeyAreaCodeSchema>;

export const journeyMilestoneCodeSchema = z.enum([
    'DOCUMENTS',
    'POSITIONS',
    'JOB_ARCHITECTURE_CONNECTED',
    'JOB_ARCHITECTURE_SETUP',
    'JOB_ARCHITECTURE_PUBLICATION',
    'POSITION_LINKS',
    'TASK_ASSESSMENT',
    'PRIORITIZATION',
    'DEFINITION',
]);

export type JourneyMilestoneCode = z.infer<typeof journeyMilestoneCodeSchema>;

export const journeyStepCodeSchema = z.enum([
    'UPLOAD_DOCUMENTS',
    'CREATE_POSITION_ARCHITECTURE',
    'CONTINUE_POSITION_SETUP',
    'RETRY_POSITION_PLAN',
    'CREATE_JOB_ARCHITECTURE',
    'CONNECT_JOB_ARCHITECTURE',
    'FINISH_JA_SETUP',
    'JA_GENERATION_FAILED',
    'CREATE_ROLES',
    'CREATE_LEVELS',
    'ASSIGN_REVIEWERS',
    'REVIEW_MINE',
    'PUBLISH',
    'LINK_POSITIONS',
    'REVIEW_LINK_SUGGESTIONS',
    'RETRY_LINK_APPLY',
    'RESTART_LEVEL_MAPPING',
    'ADD_LEVEL_TASKS',
    'PRIORITIZE',
    'DEFINE',
    'FOLLOW',
]);

export type JourneyStepCode = z.infer<typeof journeyStepCodeSchema>;

/** A count the user may not see comes back as null, never as 0. */
const journeyCountSchema = z.number().nullable();

export const journeyGuideStepSchema = z.enum([
    'company-information',
    'data-upload',
    'generate-job-architecture',
    'job-profile-template',
    'career-framework',
    'generate-roles',
    'assign-role-reviewers',
    'review-publish-roles',
    'generate-levels',
    'assign-level-reviewers',
    'review-publish-levels',
]);

export type JourneyGuideStep = z.infer<typeof journeyGuideStepSchema>;

export const journeyStepScopeSchema = z.enum(['ROLES', 'LEVELS']);

export type JourneyStepScope = z.infer<typeof journeyStepScopeSchema>;

export const journeyPositionArchitectureSchema = z.object({
    canEdit: z.boolean(),
    connectedJobArchitectureId: z.string().nullable(),
    connection: z.enum(['CONNECTED', 'NOT_CONNECTED', 'UNAVAILABLE']),
    id: z.string(),
    name: z.string(),
});

export type JourneyPositionArchitecture = z.infer<typeof journeyPositionArchitectureSchema>;

export const journeyJobArchitectureSchema = z.object({
    canEdit: z.boolean(),
    id: z.string(),
    resolvedBy: z.enum(['CONNECTED', 'ONLY', 'USER_SELECTED', 'MOST_RECENT']),
    title: z.string(),
});

export type JourneyJobArchitecture = z.infer<typeof journeyJobArchitectureSchema>;

export const journeyMilestoneSchema = z.object({
    code: journeyMilestoneCodeSchema,
    done: journeyCountSchema,
    state: journeyStateSchema,
    total: journeyCountSchema,
});

export type JourneyMilestone = z.infer<typeof journeyMilestoneSchema>;

export const journeyNextStepSchema = z.object({
    area: journeyAreaCodeSchema,
    code: journeyStepCodeSchema,
    connectTo: z.object({ id: z.string(), title: z.string() }).nullable().catch(null),
    count: journeyCountSchema,
    guideStep: journeyGuideStepSchema.nullable().catch(null),
    milestone: journeyMilestoneCodeSchema,
    scope: journeyStepScopeSchema.nullable().catch(null),
    skippable: z.boolean(),
});

export type JourneyNextStep = z.infer<typeof journeyNextStepSchema>;

const optionalNextStepSchema = journeyNextStepSchema.nullable().catch(null);

export const journeyAreaSchema = z.object({
    area: journeyAreaCodeSchema,
    milestones: z.array(journeyMilestoneSchema),
    nextStep: optionalNextStepSchema,
    state: journeyStateSchema,
});

export type JourneyArea = z.infer<typeof journeyAreaSchema>;

export const journeyKnowledgeBaseFactsSchema = z.object({
    documents: journeyCountSchema,
    failed: journeyCountSchema,
    lastUploadedAt: z.string().nullable(),
    processing: journeyCountSchema,
});

export type JourneyKnowledgeBaseFacts = z.infer<typeof journeyKnowledgeBaseFactsSchema>;

export const journeyPositionFactsSchema = z.object({
    plan: z
        .object({
            id: z.string(),
            seatsCreated: journeyCountSchema,
            seatsTotal: journeyCountSchema,
            status: z.string(),
        })
        .nullable(),
    total: journeyCountSchema,
});

export type JourneyPositionFacts = z.infer<typeof journeyPositionFactsSchema>;

export const journeyItemCountsSchema = z.object({
    assigned: z.number(),
    awaitingReview: z.number(),
    draft: z.number(),
    published: z.number(),
    reviewed: z.number(),
});

export type JourneyItemCounts = z.infer<typeof journeyItemCountsSchema>;

const optionalItemCountsSchema = journeyItemCountsSchema.nullable().catch(null);

export const journeyJobArchitectureFactsSchema = z.object({
    assigned: journeyCountSchema,
    awaitingReview: journeyCountSchema,
    draft: journeyCountSchema,
    generating: z.boolean().nullable(),
    generationFailed: z.boolean().nullable(),
    guideStep: journeyGuideStepSchema.nullable().catch(null),
    levelCounts: optionalItemCountsSchema,
    levels: journeyCountSchema,
    myReviews: journeyCountSchema,
    published: journeyCountSchema,
    reviewed: journeyCountSchema,
    roleCounts: optionalItemCountsSchema,
    roles: journeyCountSchema,
});

export type JourneyJobArchitectureFacts = z.infer<typeof journeyJobArchitectureFactsSchema>;

export const journeyLevelLinkFactsSchema = z.object({
    connectedHasLevels: z.boolean().nullable(),
    linked: journeyCountSchema,
    linkedElsewhere: journeyCountSchema,
    run: z
        .object({
            id: z.string(),
            pendingSuggestions: journeyCountSchema,
            status: z.string(),
            unitsDone: journeyCountSchema,
            unitsTotal: journeyCountSchema,
        })
        .nullable(),
    total: journeyCountSchema,
    unlinked: journeyCountSchema,
});

export type JourneyLevelLinkFacts = z.infer<typeof journeyLevelLinkFactsSchema>;

export const journeyAiTransformationFactsSchema = z.object({
    assessed: journeyCountSchema,
    defined: journeyCountSchema,
    highPotential: journeyCountSchema,
    prioritized: journeyCountSchema,
    tasksAssessed: journeyCountSchema,
    tasksHeld: journeyCountSchema,
    total: journeyCountSchema,
});

export type JourneyAiTransformationFacts = z.infer<typeof journeyAiTransformationFactsSchema>;

export const journeyFactsSchema = z.object({
    aiTransformation: journeyAiTransformationFactsSchema,
    jobArchitecture: journeyJobArchitectureFactsSchema,
    knowledgeBase: journeyKnowledgeBaseFactsSchema,
    levelLinks: journeyLevelLinkFactsSchema,
    positions: journeyPositionFactsSchema,
});

export type JourneyFacts = z.infer<typeof journeyFactsSchema>;

export const companyJourneySchema = z.object({
    areas: z
        .array(journeyAreaSchema.nullable().catch(null))
        .transform((areas) => areas.filter((area): area is JourneyArea => area !== null)),
    canCreateJobArchitecture: z.boolean(),
    facts: journeyFactsSchema,
    jobArchitecture: journeyJobArchitectureSchema.nullable(),
    nextStep: optionalNextStepSchema,
    positionArchitecture: journeyPositionArchitectureSchema.nullable(),
});

export type CompanyJourney = z.infer<typeof companyJourneySchema>;
